<template>
    <div class="smr-time-select">
        <div class="smr-time-select__closer" ref="timeCloser" v-if="isOpen" @click="clickHandler"></div>
        <el-time-select
            v-model="data"
            :picker-options="pickerOptions"
            :placeholder="placeholder"
            popper-class="smr-time-select__dropdown"
            :appendToBody="false"
            ref="timeSelectElement"
            :editable="!mobile"
            @focus="focusHandler"
            @change="changeHandler"
            @blur="blurHandler"
        >
        </el-time-select>
        <Icon class="smr-time-select__icon smr-time-select__icon--clock">
            <IconClock />
        </Icon>
        <Icon class="smr-time-select__icon smr-time-select__icon--chevron" :class="{ 'smr-time-select__icon--rotated': isOpen }" :height="20" :width="20">
            <IconChevronDown />
        </Icon>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Icon from '@/components/Icon.vue'
import IconClock from '@/components/icons/IconClock.vue'
import IconChevronDown from '@/components/icons/IconChevronDown.vue'
import { formatTime } from '@/utils/format'
import eventBus from '@/utils/event-bus';

export default {
    components: {
        Icon,
        IconClock,
        IconChevronDown
    },
    props: {
        value: {
            type: String,
            default: ''
        },
        pickerOptions: {
            type: Object,
            default: () => {
                return {
                    start: '00:00',
                    step: '00:15',
                    end: '23:50'
                }
            }
        },
        placeholder: {
            type: String,
            default: 'Zeit wählen'
        }
    },
    data() {
        return {
            data: '',
            isOpen: false,
        }
    },
    computed: {
        ...mapGetters(['mobile']),
    },
    watch: {
        value(newVal) {
            this.data = newVal
        }
    },
    mounted() {
        this.setInitialValue()
        this.addListeners()
    },
    destroyed() {
        this.removeListeners()
    },
    methods: {
        setInitialValue() {
            if(this.value) {
                this.data = this.value
            }
        },
        clickHandler() {
            if(this.isOpen) {
                this.isOpen = false
            }
        },
        blurHandler() {
            this.isOpen = false
        },
        focusHandler() {
            // scroll to the closest hour if value is not a 15min step
            if(!this.data) return
            const minutes = this.data.split(':')[1]
            if(minutes % 15 !== 0) {
                const closestFullHour = `${this.value.split(':')[0]}:00`
                setTimeout(() => {
                    const dropdownEl = this.$refs.timeSelectElement.$el.querySelector('.smr-time-select__dropdown')
                    const matchingTimeEl = [...dropdownEl.querySelectorAll(`.time-select-item`)].find(a => a.textContent.includes(closestFullHour))
                    const matchingTimeElTopOffset = matchingTimeEl.offsetTop
                    const scrollingEl = dropdownEl.querySelector('.el-scrollbar__wrap')
                    scrollingEl.scrollTop = matchingTimeElTopOffset
                }, 10);
            }
            setTimeout(() => {
                this.isOpen = true
            }, 10);
        },
        changeHandler() {
            this.emitChange()
        },
        keyDownHandler(e) {
            this.handleKeyboardEvent(e)
        },
        addListeners() {
            if(this.$refs.timeSelectElement) {
                const inputEl = this.$refs.timeSelectElement.$el.querySelector('input')
                inputEl.addEventListener('keydown', this.keyDownHandler)
            }
        },
        removeListeners() {
           if(this.$refs.timeSelectElement) {
                const inputEl = this.$refs.timeSelectElement.$el.querySelector('input')
                inputEl.removeEventListener('keydown', this.keyDownHandler)
            } 
        },
        emitChange() {
            this.$emit('change', this.data);
        },
        handleKeyboardEvent(e) {
            const target = e.currentTarget
            const value = target.value;
            
            // when pressing enter, round to the closest 15min value
            if(e.key === 'Enter') {
                if(value !== '' && value.includes(':')) {
                    let hours = parseInt(value.split(':')[0])
                    let minutes = parseInt(value.split(':')[1])
                    let invalidValue = false

                    if(isNaN(hours) || isNaN(minutes)) {
                        hours = new Date().getHours();
                        minutes = new Date().getMinutes()
                    }
                    if(hours >= 24) {
                        hours = '23'
                        invalidValue = true
                    }
                    if(minutes % 15 !== 0) {
                        invalidValue = true
                        const roundedTime = this.roundTime(hours, minutes, 15)
                        hours = roundedTime.hours
                        minutes = roundedTime.minutes
                    }
                    if(invalidValue) {
                        this.data = `${hours}:${minutes === 0 ? '00' : minutes}`
                        target.value = this.data
                    }
                } else {
                    eventBus.$emit('setCurrentTime');
                    this.data = formatTime(new Date());
                    target.value = this.data
                }
            }
            if(value === '') {
                this.setInitialValue()
            }
        },
        roundTime(hours, minutes, minutesToRound) {
            // Convert hours and minutes to minutes
            let time = (hours * 60) + minutes; 
            let rounded = Math.round(time / minutesToRound) * minutesToRound;

            let roundedHours = Math.floor(rounded / 60)
            let roundedMinutes = rounded % 60

            return { hours: roundedHours, minutes: roundedMinutes }
        }
    }
}
</script>

<style lang="scss">
.smr-time-select {
    position: relative;

    &__closer {
        @include fillout;
        z-index: 3;
    }

    &__icon {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        color: $color-grey;
        pointer-events: none;
        transition: transform 250ms $ease-out-quad;

        &--clock {
            left: 19px;
        }
        &--chevron {
            right: 16px;
        }

        &--rotated {
            transform: translateY(-50%) rotate(-180deg);
        }
    }

    &__dropdown {
        border-radius: 8px;
        box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.21);
        border: solid 1px rgba(88, 88, 88, 0.21);

        .el-scrollbar__view {
            padding: 8px 0;
        }

        .time-select-item {
            padding: 8px 20px 8px 40px;
            margin: 0 16px;
            transition: background 0.15s,  color 0.15s;
            color: $color-black;

            .touch & {
                transition: none !important;
            }

            .no-touch &:hover {
                background: $color-black-005;
            }

            &.selected {
                color: $color-black;;
            }

            &:not(:last-child) {
                border-bottom: 1px solid $color-black-005;
            }
        }

        .popper__arrow {
            display: none;
        }
    }

    .el-date-editor {
        width: 100%;

        .el-input {
            &__inner {
                border-color: $color-black-005;
                border-radius: 8px;
                height: 44px;
                line-height: 44px;
                color: $color-black;
                font-size: 15px;
                padding-left: 56px;

                &:focus {
                    border-color: $color-yellow;
                }
            }
            &__icon {
                display: none;
            }
        }
    }
}
</style>